/// <amd-module name="InstantSearch/Shared/SearchSuggestions" />
import * as React from "react";
import { IObservableArrayEventArgs, IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { getContributedItems } from "VSS/Features/PlatformUI/ContributedItemUtil";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IMenuItem, Menu } from "VSSUI/Menu";
import * as Constants from "InstantSearch/Shared/Constants";
import * as Resources from "InstantSearch/Shared/Resources";
import { IExecuteSearchService } from "InstantSearch/Shared/Services/ExecuteSearchService";
import { IInstantSearchMetadataService } from "InstantSearch/Shared/Services/InstantSearchMetadataService";
import { IInvokedProvidersManager } from "InstantSearch/Shared/Services/InvokedProvidersManager";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IInstantSearchMenuService, IInstantSearchSuggestionProps } from "InstantSearch/Shared/Types";
import { getQueryTextWithWildCardChar, isIE, getSetting } from "InstantSearch/Shared/Utils";

export interface IInstantSearchSuggestionsComponentProps extends IInstantSearchSuggestionProps { }

export interface IInstantSearchSuggestionsComponentState {
    menuItems: IReadonlyObservableArray<IMenuItem>;
}

export class InstantSearchSuggestionsComponent extends VssComponent<IInstantSearchSuggestionsComponentProps, IInstantSearchSuggestionsComponentState> {
    private _telemetryWriter: TelemetryWriter;
    private _removeManualFocus = true;
    private _isMounted = false;
    private _isFirstCall = true;
    private _debouncedUpdate: () => void;
    private _menuItems: IReadonlyObservableArray<IMenuItem> = new ObservableArray<IMenuItem>();
    private _contributionId: string;

    constructor(props: IInstantSearchSuggestionsComponentProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { menuItems: new ObservableArray<IMenuItem>() };
        const { featureName } = props;
        this._telemetryWriter = new TelemetryWriter(this.context.pageContext, featureName);

        let debounceDelay = getSetting<number>(context.pageContext, Constants.InstantSearchDebouncedDelayInMs, Constants.KeyDebounceTimeMs);

        if (isFeatureFlagEnabled(this.context.pageContext, Constants.InstantSearchDelayFeature, false)) {
            debounceDelay = Constants.KeyDebounceTimeMs2;
        }

        this._debouncedUpdate = new TimerManagement().debounce(this._updateMenuItems, debounceDelay);
    }

    public render(): JSX.Element {
        const { menuItems } = this.state;

        return <Menu className="instant-search-suggestions" id="instant-search-menu" items={menuItems} />;
    }

    public componentDidMount(): void {
        const metadataService = this.context.pageContext.getService<IInstantSearchMetadataService>("IInstantSearchMetadataService");
        this._contributionId = metadataService.menuContributionId;
        metadataService.fetchInstantSearchMenuContribution().then(() => {
            this._isMounted = true;
            const invocationTelemetryName = this.props.featureName + "Invoked";
            this._telemetryWriter.publish(invocationTelemetryName);
            this.props.searchInput.subscribe(this._debouncedOnResultsUpdate);
            this._updateMenuItems();
            this._menuItems.subscribe(this._refreshMenuItems);
            this.props.searchInput.setHelpVisibility(false);
        });
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
        this.props.searchInput.unsubscribe(this._debouncedOnResultsUpdate);
        this._menuItems.unsubscribe(this._refreshMenuItems);
        this._clearMenuItems();
    }

    public invokeActionOfSelectedItem = (): void => {
        const currentFocusedItemList = this.state.menuItems.value.filter(item => item.className && item.className.includes("focused"));
        const currentFocusedItem = currentFocusedItemList && currentFocusedItemList.length === 1 ? currentFocusedItemList[0] : undefined;

        if (currentFocusedItem && currentFocusedItem.onActivate) {
            currentFocusedItem.onActivate(currentFocusedItem);
        } else {
            const { searchInput } = this.props;
            const searchText = getQueryTextWithWildCardChar(searchInput.queryText());
            const executeSearchService = this.context.pageContext.getService<IExecuteSearchService>("IExecuteSearchService");
            executeSearchService.executeSearch(searchText, undefined, undefined, false, () => {
                this._telemetryWriter.publish("ImmersiveSearchPerformed");
            });
        }
    };

    private _clearMenuItems(): void {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const instantSearchProviders = getContributedItems(this.context.pageContext, this._contributionId, "menuId", "ms.vss-web.menu-provider");
        for (const instantSearchProvider of instantSearchProviders) {
            const menuService = contributionService.getServiceSync<IInstantSearchMenuService>(instantSearchProvider.id, true);
            if (menuService && menuService.clearMenuItems) {
                menuService.clearMenuItems();
            }
        }
    }

    private _debouncedOnResultsUpdate = (): void => {
        const { searchInput } = this.props;
        const searchText = searchInput.queryText();
        const isEmptyText = !searchText;
        const isMultipleChar = !!searchText && searchText.length > 1;
        this._isFirstCall = (this._isFirstCall && isMultipleChar) || isEmptyText;

        if (this._isFirstCall) {
            this._updateMenuItems();
        } else {
            this._debouncedUpdate();
        }
    };

    private _updateMenuItems = (): void => {
        // Get which menu providers need to be invoked
        const { searchInput } = this.props;
        const isQuickNavigationFFOn = isFeatureFlagEnabled(this.context.pageContext, "WebAccess.Search.WorkItem.QuickNavigation", false);
        const invokedProvidersManager = this.context.pageContext.getService<IInvokedProvidersManager>("IInvokedProvidersManager");
        const invokedProvidersInfo = invokedProvidersManager.getProvidersToInvoke(searchInput, this._telemetryWriter);
        const { providerMenuIds, invokedProvidersProps } = invokedProvidersInfo;
        const isQuickNavigationOn = invokedProvidersManager.isQuickNavigationModeOn();
        const { queryText, setPlaceholder } = searchInput;

        if (isQuickNavigationFFOn && !queryText().trim() && !isQuickNavigationOn && setPlaceholder) {
            setPlaceholder(Resources.QuickNavigationPlaceholder);
        }

        this._menuItems = getMenuItems(this.context.pageContext, this._contributionId, {
            searchInput: this.props.searchInput,
            hubName: this.props.hubName,
            telemetryWriter: this._telemetryWriter,
            onFocus: this._clearManualFocus,
            providerMenuIds: providerMenuIds,
            invokedProvidersProps: invokedProvidersProps
        });
        this._removeManualFocus = true;
        this._refreshMenuItems();
    };

    private _refreshMenuItems = (value?: IObservableArrayEventArgs<IMenuItem>, action?: string): void => {
        if (this._isMounted) {
            this.setState({ menuItems: this._menuItems });
        }
    };

    private _clearManualFocus = (index: number, event: React.FocusEvent<HTMLElement>): void => {
        if (this._removeManualFocus) {
            this._removeManualFocus = false;
            let needToUpdate = false;
            this._menuItems.value.forEach(item => {
                if (item.className && item.className === "focused") {
                    item.className = "";
                    needToUpdate = true;
                }
            });

            if (needToUpdate && isIE()) {
                // IE schedules _onFocus after render is complete. Hence,
                // we need to update component for focus correction.
                this.forceUpdate();
            }
        }
    };
}
